import React from "react";
import { Users, EnvelopeSimple, Phone, WhatsappLogo, ShareNetwork, ChatCircle, CalendarCheck } from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";

/** Tab "Contatti ricevuti" — iter 275 round 52.
 *
 * Mostra DUE liste distinte:
 * 1. Visite prenotate (db.viewings) — chi ha cliccato "Prenota sopralluogo".
 * 2. Contatti email (db.leads) — chi ha solo lasciato email per sbloccare i dettagli.
 *
 * Prima esisteva solo la lista viewings, ed era poco chiaro. Ora vediamo
 * entrambe le tipologie con etichette esplicite + CTA WhatsApp/email per
 * ognuna.
 */
export default function LeadTab() {
  const { id, property, listing, viewings, leads, onUpdateViewing, setTab } = usePropertyDetail();

  // Dedupe: se un lead ha anche prenotato una visita (stessa email), lo mostriamo
  // solo nella sezione visite per evitare doppioni confusionari.
  const viewingEmails = new Set(viewings.map((v) => (v.contact_email || "").toLowerCase()));
  const emailOnlyLeads = (leads || []).filter(
    (l) => !viewingEmails.has((l.email || "").toLowerCase()),
  );

  const totalCount = viewings.length + emailOnlyLeads.length;

  if (!listing?.exists) {
    return (
      <div className="mt-8" data-testid="leads-empty-no-listing">
        <div className="card-flat p-8 max-w-2xl">
          <Users size={28} weight="thin" className="text-[#C8C5BD] mb-3" />
          <h3 className="font-editorial text-2xl mb-2">
            Rendi prima l'annuncio <span className="italic-accent text-[#7C8B6E]">online</span>
          </h3>
          <p className="text-sm text-[#6B6A65] mb-5 leading-relaxed">
            I contatti arrivano dalla pagina pubblica dell'annuncio.
            Per riceverli devi prima renderlo online e condividerlo.
          </p>
          <button onClick={() => setTab("condivisione")} className="btn-primary" data-testid="lead-go-share">
            <ShareNetwork size={16} weight="bold" /> Rendi annuncio online
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6" data-testid="leads-section">
      <div>
        <span className="overline">Contatti ricevuti</span>
        <h3 className="font-editorial text-2xl mt-2">
          {totalCount === 0
            ? "Nessun contatto ancora."
            : `${totalCount} ${totalCount === 1 ? "contatto" : "contatti"}`}
        </h3>
        <p className="mt-2 text-sm text-[#6B6A65] max-w-2xl leading-relaxed">
          Qui trovi chi ha chiesto info su questo annuncio: chi ha lasciato
          l'email per sbloccare prezzo e indirizzo, e chi ha prenotato un sopralluogo.
        </p>
      </div>

      {/* ─── Sezione 1: visite prenotate ─── */}
      {viewings.length > 0 && (
        <section className="card-flat p-6 sm:p-8" data-testid="leads-viewings-section">
          <div className="flex items-center gap-2 mb-4">
            <CalendarCheck size={18} weight="duotone" className="text-[#7C8B6E]" />
            <h4 className="font-semibold text-[15px] text-[#1C1C1A]">
              Hanno prenotato una visita
            </h4>
            <span className="text-[12px] text-[#6B6A65]">· {viewings.length}</span>
          </div>
          <div className="space-y-3">
            {viewings.map((v) => {
              const dt = (() => { try { return new Date(v.requested_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }); } catch { return v.requested_at; } })();
              const waLink = v.contact_phone
                ? `https://wa.me/${v.contact_phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Buongiorno ${v.contact_name}, sono ${property?.agency_name || "l'agente"} - ho ricevuto la sua richiesta di sopralluogo per il ${dt}. Possiamo confermare?`)}`
                : null;
              const isNew = v.status === "new" || !v.status;
              return (
                <div
                  key={v.id}
                  className={`p-4 rounded-lg border ${isNew ? "border-[#7C8B6E]/40 bg-[#F1F4ED]/30" : "border-[#EAE7DF] bg-white"}`}
                  data-testid={`viewing-${v.id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[14.5px] text-[#1C1C1A]">{v.contact_name}</span>
                        {isNew && (
                          <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full bg-[#7C8B6E] text-white">
                            Nuovo
                          </span>
                        )}
                        {!isNew && (
                          <span className="text-[10.5px] uppercase text-[#6B6A65]">{v.status}</span>
                        )}
                      </div>
                      <div className="mt-1 text-[12.5px] text-[#6B6A65] space-y-0.5">
                        <a href={`mailto:${v.contact_email}`} className="hover:underline inline-flex items-center gap-1">
                          <EnvelopeSimple size={11} /> {v.contact_email}
                        </a>
                        {v.contact_phone && (
                          <div className="inline-flex items-center gap-1 ml-3">
                            <Phone size={11} /> {v.contact_phone}
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-[12px] text-[#9A968B]">Richiesta del {dt}</div>
                      {v.notes && <div className="mt-1.5 text-[12.5px] text-[#6B6A65] italic">"{v.notes}"</div>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {waLink && (
                        <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] bg-[#25D366] hover:bg-[#1FB258] text-white px-3 py-1.5 rounded-md font-medium" data-testid={`wa-${v.id}`}>
                          <WhatsappLogo size={13} weight="fill" /> WhatsApp
                        </a>
                      )}
                      {isNew && (
                        <button onClick={() => onUpdateViewing(v.id, "confirmed")} className="text-[12px] text-[#7C8B6E] hover:underline font-medium" data-testid={`confirm-${v.id}`}>
                          Conferma visita
                        </button>
                      )}
                      {v.status !== "done" && v.status !== "cancelled" && (
                        <button onClick={() => onUpdateViewing(v.id, "done")} className="text-[12px] text-[#6B6A65] hover:underline" data-testid={`done-${v.id}`}>
                          Fatto
                        </button>
                      )}
                      {v.status !== "cancelled" && (
                        <button onClick={() => onUpdateViewing(v.id, "cancelled")} className="text-[12px] text-[#9A968B] hover:underline" data-testid={`cancel-${v.id}`}>
                          Annulla
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── Sezione 2: email-only leads ─── */}
      {emailOnlyLeads.length > 0 && (
        <section className="card-flat p-6 sm:p-8" data-testid="leads-emailonly-section">
          <div className="flex items-center gap-2 mb-4">
            <ChatCircle size={18} weight="duotone" className="text-[#7C8B6E]" />
            <h4 className="font-semibold text-[15px] text-[#1C1C1A]">
              Hanno lasciato l'email
            </h4>
            <span className="text-[12px] text-[#6B6A65]">· {emailOnlyLeads.length}</span>
          </div>
          <p className="text-[12.5px] text-[#6B6A65] mb-4 leading-relaxed max-w-2xl">
            Hanno sbloccato i dettagli dell'annuncio ma non hanno ancora prenotato una visita.
            Mandagli un messaggio per capire se sono davvero interessati.
          </p>
          <div className="space-y-3">
            {emailOnlyLeads.map((l) => {
              const dt = (() => { try { return new Date(l.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }); } catch { return l.created_at; } })();
              const waLink = l.phone
                ? `https://wa.me/${String(l.phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Buongiorno ${l.name || ""}, sono l'agente referente per l'annuncio "${property?.title || "che ha visualizzato"}". Le scrivo per chiederle se desidera maggiori informazioni o organizzare una visita.`)}`
                : null;
              return (
                <div
                  key={l.id}
                  className="p-4 rounded-lg border border-[#EAE7DF] bg-white"
                  data-testid={`emaillead-${l.id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[14.5px] text-[#1C1C1A]">{l.name || "(senza nome)"}</div>
                      <div className="mt-1 text-[12.5px] text-[#6B6A65] space-y-0.5">
                        <a href={`mailto:${l.email}`} className="hover:underline inline-flex items-center gap-1">
                          <EnvelopeSimple size={11} /> {l.email}
                        </a>
                        {l.phone && (
                          <div className="inline-flex items-center gap-1 ml-3">
                            <Phone size={11} /> {l.phone}
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-[12px] text-[#9A968B]">Contatto del {dt}</div>
                      {l.message && <div className="mt-1.5 text-[12.5px] text-[#6B6A65] italic">"{l.message}"</div>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {waLink && (
                        <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] bg-[#25D366] hover:bg-[#1FB258] text-white px-3 py-1.5 rounded-md font-medium" data-testid={`wa-lead-${l.id}`}>
                          <WhatsappLogo size={13} weight="fill" /> WhatsApp
                        </a>
                      )}
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-[12px] text-[#7C8B6E] hover:underline font-medium" data-testid={`email-lead-${l.id}`}>
                        <EnvelopeSimple size={13} /> Rispondi
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── Empty state (listing attivo ma 0 contatti) ─── */}
      {totalCount === 0 && (
        <div className="card-flat p-8 text-center">
          <Users size={32} weight="thin" className="text-[#C8C5BD] mx-auto mb-3" />
          <p className="text-sm text-[#6B6A65] max-w-md mx-auto leading-relaxed">
            Quando un visitatore lascia la sua email per vedere i dettagli o
            prenota una visita dalla pagina pubblica, lo vedrai qui in tempo reale.
            Ti arriverà anche una notifica push e una email di promemoria.
          </p>
          <button
            onClick={() => setTab("condivisione")}
            className="mt-4 text-[13px] text-[#7C8B6E] hover:underline font-medium"
            data-testid="lead-empty-share"
          >
            Condividi la pagina annuncio →
          </button>
        </div>
      )}
    </div>
  );
}
